import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { RevealText } from "./Reveal";
import { IMAGES } from "../../lib/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden pt-32 lg:pt-0 flex items-center">
      {/* radial glow */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#00D166]/10 blur-[130px]" />
      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center relative">
        {/* Left kinetic text */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#00D166]" />
            <span className="font-body text-xs tracking-[0.35em] text-[#00D166]">EXECUTIVE SEARCH · HR CONSULTING</span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter leading-none text-5xl md:text-6xl lg:text-7xl text-white">
            <RevealText lines={["BUILDING"]} delay={0.4} />
            <span className="block text-[#00D166]">
              <RevealText lines={["ELITE"]} delay={0.52} />
            </span>
            <RevealText lines={["LEADERSHIP"]} delay={0.64} />
            <span className="block font-serifed italic font-normal text-white/70 text-4xl md:text-5xl lg:text-6xl">
              <RevealText lines={["globally."]} delay={0.78} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-md font-body text-white/60 text-base leading-relaxed"
            data-testid="hero-subtitle"
          >
            A Delhi-based executive search & HR consulting firm placing transformational leaders across India, the UAE and the US.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#contact" data-testid="hero-cta-primary" className="group font-body text-sm font-semibold bg-[#00D166] text-[#030712] px-7 py-4 hover:bg-white transition-colors duration-300">
              Start a Search
            </a>
            <a href="#services" data-testid="hero-cta-secondary" className="font-body text-sm font-semibold text-white border border-white/20 px-7 py-4 hover:border-[#00D166] hover:text-[#00D166] transition-colors duration-300">
              Explore Services
            </a>
          </motion.div>
        </motion.div>

        {/* Right image */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1], delay: 0.5 }}
            className="relative aspect-[3/4] overflow-hidden border border-white/10"
          >
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src={IMAGES.office}
              alt="Luxury boardroom"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div className="font-serifed italic text-white/80 text-xl">Est. Delhi</div>
              <div className="font-display text-[#00D166] text-2xl font-bold">YG</div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#services"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-6 lg:left-10 flex items-center gap-2 text-white/50 hover:text-[#00D166] transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
        <span className="font-body text-xs tracking-[0.3em]">SCROLL</span>
      </motion.a>
    </section>
  );
}
